import { useId, type FormEvent } from "react";
import type { MoneyAccount } from "@/api/cash";
import type { Expense, ExpenseStatusInput } from "@/api/finance";
import { RequestError } from "@/components/RequestError";
import { Button } from "@/components/ui/button";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { todayIso } from "@/lib/dates";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type ExpenseStatusFormProps = {
  expense: Expense;
  accounts?: MoneyAccount[];
  onSubmit: (input: ExpenseStatusInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

/** An unpaid bill was paid, from which account; or a bill someone paid from their own pocket
 *  was repaid to them (the repayment itself is a transfer; this only settles the bill). */
export function ExpenseStatusForm({
  expense,
  accounts,
  onSubmit,
  onCancel,
  onReload,
}: ExpenseStatusFormProps) {
  const id = useId();
  const unpaid = expense.status === "unpaid";
  const openAccounts = accounts?.filter((a) => !a.archived_at) ?? [];
  const defaultAccount = openAccounts.find(
    (a) => a.is_default_out && (a.currency === expense.currency || a.currency === "INR"),
  );
  const paidFrom = openAccounts.find((a) => a.id === expense.paid_from_id);
  const { values, set } = useFormValues({
    paid_on: todayIso(),
    paid_from_id: defaultAccount?.id ?? "",
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        paid_on: values.paid_on,
        paid_from_id: unpaid ? values.paid_from_id || null : null,
      }),
    );
  }

  if (!unpaid && paidFrom?.kind === "person") {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          {paidFrom.name} paid this from their own pocket. Pay them back on Money → Owed to
          people: that records the repayment and marks the bills it covers reimbursed.
        </p>
        <div>
          <Button variant="outline" onClick={onCancel}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      {!unpaid && (
        <p className="text-sm text-muted-foreground">
          {expense.payee || "The person"} paid this from their own pocket. Record the repayment
          to them as a transfer on the Money page; this only marks the bill as settled.
        </p>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <TextField
          id={`${id}-on`}
          label={unpaid ? "Paid on" : "Repaid on"}
          type="date"
          value={values.paid_on}
          onChange={(value) => set("paid_on", value)}
          error={errors.paid_on}
          hint={`The bill is dated ${expense.spent_on}`}
        />
        {unpaid && (
          <SelectField
            id={`${id}-from`}
            label="Paid from"
            value={values.paid_from_id}
            onChange={(value) => set("paid_from_id", value)}
            options={openAccounts.map((a) => ({ value: a.id, label: `${a.name} (${a.currency})` }))}
            placeholder="Choose an account"
            error={errors.paid_from_id}
            hint="Which money account it left"
          />
        )}
      </div>
      <FormActions
        saving={saving}
        submitLabel={unpaid ? "Mark paid" : "Mark reimbursed"}
        onCancel={onCancel}
        disabled={unpaid && !values.paid_from_id}
      />
    </form>
  );
}
